/**
 * @ngdoc type
 * @module flowingly.runner.maintenance
 * @name runnerMaintenanceController
 *
 * @description Controller for Maintenance page.
 */
import { SharedAngular } from '@Client/@types/sharedAngular';
import angular, { IScope } from 'angular';
import { IStateService } from 'angular-ui-router';
import {
  BUSINESS_UPDATED_EVENT,
  BUSINESS_TERMINATED_POSTFIX
} from './business/runner.maintenance.business.controller';
import IBusinessDetail from '@Shared.Angular/@types/core/contracts/queryModel/business/businessDetail';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';

angular
  .module('flowingly.runner.login')
  .controller('runnerMaintenanceController', runnerMaintenanceController);

runnerMaintenanceController.$inject = [
  '$state',
  'permissionsService',
  'authService',
  'maintenanceService',
  '$scope'
];

enum MaintenanceTabId {
  EMAILS = 'emails',
  SETTINGS = 'settings',
  APPLICATION_SETTINGS = 'applicationsettings',
  FLOWS = 'flows',
  FLOW_MODELS = 'flowmodels',
  VALIDATION = 'validation',
  USERS = 'users',
  BUSINESS = 'business',
  REPORTS = 'reports'
}

function runnerMaintenanceController(
  $state: IStateService,
  permissionsService: SharedAngular.PermissionsService,
  authService: AuthService,
  maintenanceService: MaintenanceService,
  $scope: IScope
) {
  if ($state.current.name.startsWith('app.runner.maintenance')) {
    if (
      permissionsService.currentUserHasPermission(
        FlowinglyPermissions.ADMIN_ACCESS
      )
    ) {
      if ($state.current.name === 'app.runner.maintenance') {
        $state.go(STATE_MAPPINGS.maintenanceSettings);
      }
    } else {
      $state.go(STATE_MAPPINGS.flowsactive);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-this-alias
  const $ctrl = this;
  $ctrl.businesses = [];
  $ctrl.includeTerminated = false;
  $ctrl.hasActionsPermission = permissionsService.currentUserHasPermission(
    FlowinglyPermissions.ADMIN_ACTIONS
  );
  $ctrl.hasEmailActionPermission =
    permissionsService.currentUserHasAnyOfPermissions([
      FlowinglyPermissions.ADMIN_ACTIONS,
      FlowinglyPermissions.ADMIN_ACTIONS_EMAIL
    ]);

  const allTabs = [
    {
      id: MaintenanceTabId.EMAILS,
      label: 'Emails',
      stateName: STATE_MAPPINGS.maintenanceEmails,
      isHidden: !$ctrl.hasEmailActionPermission
    },
    {
      id: MaintenanceTabId.SETTINGS,
      label: 'Settings',
      stateName: STATE_MAPPINGS.maintenanceSettings
    },
    {
      id: MaintenanceTabId.APPLICATION_SETTINGS,
      label: 'App Settings',
      stateName: STATE_MAPPINGS.maintenanceApplicationSettings
    },
    {
      id: MaintenanceTabId.FLOWS,
      label: 'Flows',
      stateName: STATE_MAPPINGS.maintenanceFlows
    },
    {
      id: MaintenanceTabId.FLOW_MODELS,
      label: 'Flow Models',
      stateName: STATE_MAPPINGS.maintenanceFlowModels
    },
    {
      id: MaintenanceTabId.VALIDATION,
      label: 'Validation',
      stateName: STATE_MAPPINGS.maintenanceValidation,
      isHidden: !$ctrl.hasActionsPermission
    },
    {
      id: MaintenanceTabId.USERS,
      label: 'Users',
      stateName: STATE_MAPPINGS.maintenanceUsers
    },
    {
      id: MaintenanceTabId.BUSINESS,
      label: 'Business',
      stateName: STATE_MAPPINGS.maintenanceBusiness
    },
    {
      id: MaintenanceTabId.REPORTS,
      label: 'Reports',
      stateName: STATE_MAPPINGS.maintenanceReports
    }
  ];

  $ctrl.tabItems = allTabs;

  const updateSelectedTabId = () => {
    const currentStateName = $state.current.name;
    const tab = allTabs.find((tab) => tab.stateName === currentStateName);
    $ctrl.selectedTabId = tab ? tab.id : allTabs[0].id;
  };

  updateSelectedTabId();

  $ctrl.onTabChange = (newTabId: string) => {
    const tab = allTabs.find((tab) => tab.id === newTabId);
    if (tab && tab.stateName) {
      $state.go(tab.stateName);
    }
  };

  $scope.$on('$stateChangeSuccess', () => {
    updateSelectedTabId();
  });
  $scope.$on(
    BUSINESS_UPDATED_EVENT,
    (event: angular.IAngularEvent, updatedBusiness: IBusinessDetail) => {
      event.stopPropagation();
      if (!updatedBusiness) {
        return;
      }
      const businessIndex = $ctrl.businesses.findIndex(
        (b) => b.id === updatedBusiness.id
      );
      if (businessIndex < 0) {
        return;
      }
      $ctrl.businesses.splice(businessIndex, 1, updatedBusiness);
      if ($ctrl.selectedBusiness.id === updatedBusiness.id) {
        $ctrl.selectedBusiness = updatedBusiness;
      }
      angular
        .element(
          document
            .evaluate(
              "//kendo-combo-box-facade[@id='businesses']//select",
              document
            )
            .iterateNext()
        )
        .data('kendoComboBox')
        .search(updatedBusiness.name);
    }
  );

  const businessesPromise = authService.getUserDeferred().then((user) => {
    return maintenanceService.getBusinesses().then((businesses) => {
      for (const business of businesses) {
        if (!business.active) {
          business.name += BUSINESS_TERMINATED_POSTFIX;
        }
      }
      $ctrl.businesses = businesses.sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      const userBusiness = businesses.find(
        (business) => business.id === user.businessId
      );
      $ctrl.selectedBusiness = userBusiness;
      setTimeout(() => {
        const inputElement = document
          .evaluate(
            '//kendo-combo-box-facade[@id="businesses"]//input',
            document
          )
          .iterateNext() as HTMLInputElement | null;

        if (inputElement) {
          inputElement.focus();
          inputElement.select();
        }
      }, 100);
      return businesses;
    });
  });

  $ctrl.getFilteredBusinesses = (search) => {
    const searchTerm = search.term?.toLowerCase();

    if (!searchTerm) {
      return businessesPromise.then(() => {
        if ($ctrl.includeTerminated) {
          return $ctrl.businesses;
        }
        return $ctrl.businesses.filter((business) => business.active);
      });
    }

    let filteredBusinesses = $ctrl.businesses;
    if (!$ctrl.includeTerminated) {
      filteredBusinesses = filteredBusinesses.filter(
        (business) => business.active
      );
    }

    return filteredBusinesses.filter(
      (business) => business.name?.toLowerCase().indexOf(searchTerm) > -1
    );
  };

  $ctrl.onIncludeTerminatedChange = () => {
    if (
      !$ctrl.includeTerminated &&
      $ctrl.selectedBusiness &&
      !$ctrl.selectedBusiness.active
    ) {
      $ctrl.selectedBusiness = null;
    }

    // Refresh the business list in the combo box
    const comboBox = angular
      .element(
        document
          .evaluate(
            "//kendo-combo-box-facade[@id='businesses']//select",
            document
          )
          .iterateNext()
      )
      .data('kendoComboBox');
    if (comboBox) {
      comboBox.dataSource.read();
    }
  };
}
